import type { ObjectId } from '@racemap/sdk/schema/base';
import type { Immutable } from 'immer';
import { SortDirection, type SortDirectionType } from 'react-virtualized';
import { getter, keySplitter } from '../../lib/FormUtils';
import type { Entry } from './DataTable';
import type { BasicColumnProps as ColumnProps } from './columns';

export function safeSorting<T extends Record<string, unknown | null | undefined>>(
  data: Array<T>,
  key: string,
  sort: (a: T, b: T) => number,
): Array<T> {
  const dataCopy = [...data];
  const get = (root: T) => getter(keySplitter(key), root);

  return dataCopy.sort((a, b) => {
    if (get(a) == null) return 1;
    if (get(b) == null) return -1;

    return sort(a, b);
  });
}

export function getSortedData<T extends Immutable<Record<string, C>>, C>(
  data: Array<T> | ReadonlyArray<T>,
  sortBy: string | undefined,
  sortDirection: SortDirectionType,
  customSortingMethods: SortingMethods<T, C>,
): Array<T> {
  const dataCopy = [...data];
  let sortedData: Array<T>;

  if (sortBy == null || data.length < 2) return dataCopy;
  const customMethod = customSortingMethods[sortBy];
  const get = (root: T) => getter(keySplitter(sortBy), root);

  if (customMethod != null) {
    // sort the data with an custom method
    sortedData = dataCopy.sort((a, b) => customMethod(get(a) as C, get(b) as C, a, b));
  } else {
    const valueWithSortyByEntry = data.find((d) => get(d) != null);
    if (valueWithSortyByEntry == null) return dataCopy;

    const exampleValue = get(valueWithSortyByEntry);
    if (exampleValue == null) return dataCopy;

    switch (typeof exampleValue) {
      case 'string':
        sortedData = safeSorting<T>(dataCopy, sortBy, (a, b) =>
          (get(a) as string) === ''
            ? 1
            : (get(b) as string) === ''
              ? -1
              : (get(a) as string).localeCompare(get(b) as string),
        );
        break;
      case 'number':
        sortedData = safeSorting(
          dataCopy,
          sortBy,
          (a, b) => (get(a) as number) - (get(b) as number),
        );
        break;
      case 'object':
        switch (exampleValue?.constructor) {
          case Date:
            sortedData = safeSorting(
              dataCopy,
              sortBy,
              (a, b) => (get(b) as Date).getTime() - (get(a) as Date).getTime(),
            );
            break;
          default:
            throw new Error('Cant sort array with object values!');
        }
        break;
      default:
        throw new Error(`Cant sort array with ${typeof exampleValue} values!`);
    }
  }

  if (sortDirection === SortDirection.ASC) return sortedData;
  return sortedData.reverse();
}

export type CustomSortingMethod<Row, Cell> = (
  cellValueA: Cell,
  cellValueB: Cell,
  rowValueA: Row,
  rowValueB: Row,
) => number;
type SortingMethods<T, C> = Record<string, CustomSortingMethod<T, C>>;

export function getSortingMethods<T extends Immutable<Record<string, unknown>>, C>(
  columns: Array<React.ReactElement<ColumnProps>>,
  selectionSortingMethod?: CustomSortingMethod<T, C>,
): SortingMethods<T, C> {
  const methods: SortingMethods<T, C> = {};

  for (const col of columns) {
    if (col.props.disableSort || col.props.sortingMethod == null) continue;

    methods[col.props.dataKey] = col.props.sortingMethod;
  }

  if (selectionSortingMethod) {
    methods._selection_ = selectionSortingMethod;
  }

  return methods;
}

export function agreggateFilter(
  children: Array<React.ReactElement<ColumnProps>>,
): Record<string, Array<string>> {
  const filter: Record<string, Array<string>> = {};
  for (const child of children) {
    if (
      child.props.columnData?.selection != null &&
      child.props.columnData.selection.length !== 0
    ) {
      filter[child.props.dataKey] = child.props.columnData.selection;
    }
  }

  return filter;
}

export function getSearchableColumns(
  children: Array<React.ReactElement<ColumnProps>>,
): Set<string> {
  const columnKeys = new Set<string>();

  for (const child of children) {
    if (child.props.columnData?.searchable) {
      columnKeys.add(child.props.dataKey);
    }
  }

  return columnKeys;
}

export function getFilteredData<T extends Entry<I>, I extends string | ObjectId>(
  data: Array<T>,
  searchValue: string,
  children: Array<React.ReactElement<ColumnProps>>,
): Array<T> {
  const filter = agreggateFilter(children);
  const searchColumns = getSearchableColumns(children);
  const dataCopy = [...data];
  const filterActive = Object.keys(filter).length !== 0;
  const searchActive = searchColumns.size > 0 && searchValue !== '';

  if (!filterActive && !searchActive) return dataCopy;

  return dataCopy.filter((e) => {
    const selected = Object.entries(filter).every(([dataKey, selections]) => {
      const path = keySplitter(dataKey);
      const value = getter<string | undefined>(path, e);

      return selections.includes(value?.toString() || '');
    });
    const searched = Array.from(searchColumns).find((dataKey) => {
      const cellValue = e[dataKey]?.toString() || '';
      return cellValue.toString().toLowerCase().includes(searchValue.toLowerCase());
    });

    return (selected || !filterActive) && (searched || !searchActive);
  });
}

export function getMinWidth(columns: Array<React.ReactElement<ColumnProps>>) {
  return columns.reduce((pW, cC) => {
    return pW + (cC.props.minWidth || 50);
  }, 0);
}

export function getUsefulWidth(
  columns: Array<React.ReactElement<ColumnProps>>,
  withRowIndex: boolean,
) {
  const offset = withRowIndex ? 90 : 60;
  return columns.reduce((pW, cC) => {
    const columnWidth =
      cC.props.width != null && cC.props.width !== 0
        ? cC.props.width
        : cC.props.minWidth != null
          ? cC.props.minWidth
          : 100;
    return pW + columnWidth;
  }, offset);
}

export const getVisibleColumnsAtDefault = (children: Array<React.ReactElement<ColumnProps>>) => {
  return children
    .filter((c) => !c.props.columnData?.hiddenOnDefault)
    .map((c) => c.props.dataKey.toString());
};
